import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Card,
  CardContent,
  CardActions,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  CheckCircleOutline,
  Cancel,
  Payment,
  Info,
  Warning,
} from '@mui/icons-material';
import { getSubscriptionStatus, createSubscription, cancelSubscription, getAvailablePlans } from '../../services/api';

interface Plan {
  id: number;
  name: string;
  price: number;
}

interface SubscriptionStatus {
  plan_type: number;
  status: string;
  current_period_end: string | null;
  is_active: boolean;
}

const SubscriptionSettings: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plansResponse, statusResponse] = await Promise.all([
        getAvailablePlans(),
        getSubscriptionStatus()
      ]);
      setPlans(plansResponse.data);
      setSubscription(statusResponse.data);
    } catch (err: any) {
      setError('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (plan: Plan) => {
    setSelectedPlan(plan);
    setShowUpgradeDialog(true);
  };

  const handleConfirmUpgrade = async () => {
    if (!selectedPlan) return;

    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);

      const response = await createSubscription({ plan_type: selectedPlan.id });
      
      // Stripeのチェックアウトページにリダイレクト
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'サブスクリプションの作成に失敗しました');
    } finally {
      setActionLoading(false);
      setShowUpgradeDialog(false);
      setSelectedPlan(null);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);

      await cancelSubscription();
      setSuccess('サブスクリプションをキャンセルしました');
      setShowCancelDialog(false);
      
      // データを再取得
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'サブスクリプションのキャンセルに失敗しました');
    } finally {
      setActionLoading(false);
    }
  };

  const getCurrentPlan = () => {
    if (!subscription) return plans.find(p => p.id === 0);
    return plans.find(p => p.id === subscription.plan_type);
  };

  const getPlanStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'canceled':
        return 'error';
      case 'past_due':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getPlanStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'アクティブ';
      case 'canceled':
        return 'キャンセル済み';
      case 'past_due':
        return '支払い遅延';
      case 'inactive':
        return '非アクティブ';
      default:
        return status;
    }
  };

  const isSubscriptionExpired = () => {
    if (!subscription || !subscription.current_period_end) return false;
    return new Date() > new Date(subscription.current_period_end);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const currentPlan = getCurrentPlan();

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        サブスクリプション管理
      </Typography>

      {/* 現在のプラン表示 */}
      <Paper elevation={0} sx={{ mb: 3, bgcolor: "background.paper" }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            現在のプラン
          </Typography>
          
          {currentPlan && (
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Chip
                label={currentPlan.name}
                color={currentPlan.id === 0 ? 'default' : 'primary'}
                variant={currentPlan.id === 0 ? 'outlined' : 'filled'}
              />
              {subscription && subscription.status !== 'inactive' && (
                <Chip
                  label={getPlanStatusText(subscription.status)}
                  color={getPlanStatusColor(subscription.status) as any}
                  size="small"
                />
              )}
            </Box>
          )}

          {subscription && subscription.current_period_end && (
            <Typography variant="body2" color="text.secondary">
              次回更新日: {new Date(subscription.current_period_end).toLocaleDateString('ja-JP')}
              {isSubscriptionExpired() && ' (期限切れ)'}
            </Typography>
          )}

          {subscription && subscription.is_active && subscription.status !== 'canceled' && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setShowCancelDialog(true)}
              sx={{ mt: 2 }}
              size="small"
            >
              サブスクリプションをキャンセル
            </Button>
          )}

          {subscription && subscription.status === 'canceled' && !isSubscriptionExpired() && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
              キャンセル済み - 期間終了までサービスをご利用いただけます
            </Typography>
          )}
        </Box>
      </Paper>

      {/* 利用可能なプラン */}
      <Paper elevation={0} sx={{ bgcolor: "background.paper" }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            利用可能なプラン
          </Typography>

          <Box display="flex" flexDirection="column" gap={2}>
            {plans
              .filter(plan => plan.id > 0) // 無料プランは除外
              .map((plan) => (
                <Card key={plan.id} variant="outlined">
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="h6">
                        {plan.name}
                      </Typography>
                      <Typography variant="h6" color="primary">
                        ¥{plan.price.toLocaleString()}/月
                      </Typography>
                    </Box>
                    
                    {currentPlan?.id === plan.id && (
                      <Chip
                        label="現在のプラン"
                        color="primary"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}
                  </CardContent>
                  <CardActions>
                    {currentPlan?.id === plan.id ? (
                      <Button disabled size="small">
                        現在のプラン
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={() => handleUpgrade(plan)}
                        size="small"
                        startIcon={<Payment />}
                      >
                        {currentPlan?.id === 0 ? 'プランを選択' : 'プランを変更'}
                      </Button>
                    )}
                  </CardActions>
                </Card>
              ))}
          </Box>
        </Box>
      </Paper>

      {/* エラー・成功メッセージ */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {success}
        </Alert>
      )}

      {/* プラン選択ダイアログ */}
      <Dialog open={showUpgradeDialog} onClose={() => setShowUpgradeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          プランの選択
        </DialogTitle>
        <DialogContent>
          {selectedPlan && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedPlan.name} プラン
              </Typography>
              <Typography variant="body1" color="primary" gutterBottom>
                ¥{selectedPlan.price.toLocaleString()}/月
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                選択したプランに変更します。Stripeの決済ページに移動します。
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUpgradeDialog(false)}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmUpgrade}
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <Payment />}
          >
            {actionLoading ? '処理中...' : 'プランを選択'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* キャンセル確認ダイアログ */}
      <Dialog open={showCancelDialog} onClose={() => setShowCancelDialog(false)}>
        <DialogTitle>
          サブスクリプションのキャンセル
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            サブスクリプションをキャンセルしますか？
          </Typography>
          <Typography variant="body2" color="text.secondary">
            キャンセル後も現在の期間終了まではサービスをご利用いただけます。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelDialog(false)}>
            キャンセル
          </Button>
          <Button
            onClick={handleCancelSubscription}
            variant="contained"
            color="error"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <Cancel />}
          >
            {actionLoading ? '処理中...' : 'キャンセルする'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionSettings; 